// components/history-sidebar.jsx
import{ useState, useEffect , useMemo } from "react";
import classNames from "classnames";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarProvider,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
} from "ui/components/sidebar";

import { Ellipsis, Share2, Copy, Check, SquarePen, Trash } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "ui/components/dropdown-menu";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "ui/components/dialog";

import { Label } from "ui/components/label";
import { Input } from "ui/components/input";
import { Button } from "ui/components/button";


export default function HistorySidebar({
  historyList = [],
  onSelect,
  selectedId,
  onDelete,
}) {
  const [copiedId, setCopiedId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 250);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const filteredHistory = useMemo(() => {
    if (!debouncedSearch.trim()) return historyList;
    return historyList.filter(chat =>
      chat.project_query_title?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [historyList, debouncedSearch]);

  const handleCopy = (e, chatId) => {
    e.preventDefault();
    const url = `${window.location.origin}/explore/${chatId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(chatId);
    setTimeout(() => {
      setCopiedId(null);
      setOpenDropdownId(null);
    }, 1500);
  };

  const handleDelete = async (e, chatId) => {
    e.preventDefault();
    e.stopPropagation();
    setPendingDeleteId(chatId);
    await onDelete(chatId);
    setPendingDeleteId(null);
    setOpenDropdownId(null);
  };

  const cancelDelete = (e) => {
    e?.stopPropagation();
    setPendingDeleteId(null);
    setOpenDropdownId(null);
  };

  return (
    <SidebarProvider className="w-fit !min-h-full !h-full">
      <Sidebar className="!relative !h-full w-[260px] border-r bg-white">
        <SidebarHeader className="flex flex-col p-2 gap-2">
          <div
            className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-zinc-200/50 rounded"
            onClick={() => onSelect(null)}>
            <SquarePen size={16} />
            <span className="text-sm font-medium">New Chat</span>
          </div>
          <input
            type="text"
            className="w-full px-3 py-2 border rounded text-sm"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase px-3 pb-1 text-gray-500">
              Chats
            </SidebarGroupLabel>

            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {filteredHistory.length ? (
                  filteredHistory.map((chat) => (
                    <SidebarMenuItem
                      key={chat.id}
                      className={`group flex justify-between group-scoped items-center px-3 py-2 text-sm rounded cursor-pointer ${
                        selectedId === chat.id ? "bg-zinc-200/50" : "hover:bg-neutral-100"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(chat.id);
                      }}>
                      <span className="truncate w-[160px]">
                        {chat.project_query_title}
                      </span>

                      <DropdownMenu
                        modal={false}
                        open={openDropdownId === chat.id}
                        onOpenChange={(open) =>
                          setOpenDropdownId(open ? chat.id : null)
                        }>
                        <DropdownMenuTrigger
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdownId(chat.id);
                          }}>
                          <Ellipsis
                            size={16}
                            className="text-gray-500 icon cursor-pointer"
                          />
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          align="start"
                          onClick={(e) => e.stopPropagation()}>
                          <ShareDialog
                            chatId={chat.id}
                            copiedId={copiedId}
                            onCopy={handleCopy}
                          />
                          <DeleteDialog
                            chatId={chat.id}
                            onDelete={handleDelete}
                            onCancel={cancelDelete}
                            isPending={pendingDeleteId === chat.id}
                          />
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  ))
                ) : (
                  <div className="text-xs text-gray-400 px-3 py-2">
                    No chats found
                  </div>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    </SidebarProvider>
  );
}


const ShareDialog = ({ chatId, copiedId, onCopy }) => {
  const shareUrl = `${window.location.origin}/explore/${chatId}`;
  return (
    <Dialog>
      <DialogTrigger className="w-full cursor-pointer flex gap-2 items-center px-2 py-1 text-sm text-left hover:bg-gray-100">
        <Share2 size={16} className="ml-1" />
        <span>Share</span>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share link</DialogTitle>
          <DialogDescription>
            Anyone with this link can view the chat.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-2">
          <div className="grid flex-1 gap-2">
            <Label htmlFor="link" className="sr-only">
              Link
            </Label>
            <Input id="link" value={shareUrl} readOnly />
          </div>
          <Button onClick={(e) => onCopy(e, chatId)} size="sm" className="px-3">
            {copiedId === chatId ? (
              <Check size={16} className="text-green-600" />
            ) : (
              <Copy size={16} />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeleteDialog = ({ chatId, onDelete, onCancel, isPending }) => (
  <Dialog>
    <DialogTrigger className="w-full cursor-pointer flex gap-2 items-center px-2 py-1 text-sm text-left hover:bg-gray-100">
      <Trash size={16} className="ml-1" />
      <span>Delete</span>
    </DialogTrigger>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogDescription>
          Are you sure? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <div className="flex justify-end gap-2 mt-4">
        <Button className="cursor-pointer" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
        className="cursor-pointer"
          variant="destructive"
          onClick={(e) => onDelete(e, chatId)}
          disabled={isPending}>
          Delete
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

